import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import "./UnitClassPreview.scss";

const blankClass = (id, name = "") => ({ id, name, code: "", nav: "", effectiveDate: "", priceMode: "nav", price: "", accepting: true });
const payload = (items, defaultId, defaultEffectiveDate = "") => items.map(item => ({
  code: item.code,
  name: item.name,
  nav_per_unit: item.nav || null,
  nav_effective_date: item.nav ? (item.effectiveDate || defaultEffectiveDate || null) : null,
  issue_price_mode: item.priceMode,
  issue_price_per_unit: item.priceMode === "fixed" ? item.price : null,
  is_default: item.id === defaultId,
  accepting_subscriptions: item.accepting,
  status: "active"
}));

export default function UnitClassPreview({ application = false, availableClasses = [], value = "", onChange, onClassesChange, defaultEffectiveDate = "" }) {
  const [enabled, setEnabled] = useState(false);
  const [classes, setClasses] = useState([blankClass(1, "Common"), blankClass(2, "Preferred")]);
  const [defaultId, setDefaultId] = useState(1);
  useEffect(() => { if (application && Array.isArray(availableClasses) && availableClasses.length === 1 && !value) onChange?.(String(availableClasses[0].id)); }, [application, availableClasses, onChange, value]);
  const publish = (next, nextDefault = defaultId) => onClassesChange?.(enabled ? payload(next, nextDefault, defaultEffectiveDate) : []);
  const update = (id, values) => setClasses(current => {
    const next = current.map(item => item.id === id ? { ...item, ...values } : item);
    publish(next);
    return next;
  });

  if (application) return <section className="yp-unit-class-preview">
    <div className="yp-unit-class-heading"><div><span className="yp-eyebrow">Unit class</span><h3>Investment unit class</h3></div></div>
    {availableClasses === null ? <div className="yp-unit-class-loading"><CircularProgress size={22} /><span>Loading available unit classes…</span></div> : availableClasses.length ? <TextField required select fullWidth label="Unit class" value={value} onChange={event => onChange?.(event.target.value)}>
      {availableClasses.map(item => <MenuItem key={item.id} value={String(item.id)}>{item.name}{item.code ? ` (${item.code})` : ""}</MenuItem>)}
    </TextField> : <Alert severity="warning">This fund has no active unit class accepting subscriptions.</Alert>}
  </section>;

  return <section className="yp-unit-class-preview">
    <div className="yp-unit-class-heading"><div><span className="yp-eyebrow">Unit classes</span><h3>Fund unit structure</h3></div></div>
    <FormControlLabel control={<Switch checked={enabled} onChange={event => {
      setEnabled(event.target.checked);
      onClassesChange?.(event.target.checked ? payload(classes, defaultId, defaultEffectiveDate) : []);
    }} />} label="Does this fund have multiple unit classes?" />
    {!enabled && <p>A Default class will be created automatically using the fund’s price and NAV.</p>}
    {enabled && <>
      <Alert severity="info">Configure at least two classes. Exactly one class must be the default.</Alert>
      <div className="yp-unit-class-fields">
        <TextField select label="Default class" value={defaultId} onChange={event => {
          const id = Number(event.target.value);
          setDefaultId(id);
          publish(classes, id);
        }}>{classes.map((item, index) => <MenuItem key={item.id} value={item.id}>{item.name || `Class ${index + 1}`}</MenuItem>)}</TextField>
        <p>Investors may hold more than one class within the same fund.</p>
      </div>
      {classes.map((item, index) => <article className="yp-unit-class-card" key={item.id}>
        <div className="yp-unit-class-heading">
          <strong>Class {index + 1}{item.id === defaultId ? " · Default" : ""}</strong>
          <Button type="button" disabled={classes.length <= 2} onClick={() => {
            const next = classes.filter(entry => entry.id !== item.id);
            const nextDefault = item.id === defaultId ? next[0].id : defaultId;
            setClasses(next); setDefaultId(nextDefault); publish(next, nextDefault);
          }}>Remove</Button>
        </div>
        <div className="yp-unit-class-fields">
          <TextField required label="Class name" value={item.name} onChange={event => update(item.id, { name: event.target.value })} />
          <TextField required label="Class code" value={item.code} onChange={event => update(item.id, { code: event.target.value.toUpperCase() })} />
          <TextField type="number" label="NAV per unit" value={item.nav} onChange={event => update(item.id, { nav: event.target.value })} inputProps={{ min: 0, step: "0.000001" }} />
          <TextField required={Boolean(item.nav)} type="date" label="NAV effective date" value={item.effectiveDate || (item.nav ? defaultEffectiveDate : "")} onChange={event => update(item.id, { effectiveDate: event.target.value })} InputLabelProps={{ shrink: true }} helperText={item.nav && !item.effectiveDate && defaultEffectiveDate ? "Using the fund start date" : "Required when NAV is provided"} />
          <TextField select label="Issue price" value={item.priceMode} onChange={event => update(item.id, { priceMode: event.target.value })}>
            <MenuItem value="nav">Use current class NAV</MenuItem><MenuItem value="fixed">Separate subscription price</MenuItem>
          </TextField>
          {item.priceMode === "fixed" && <TextField required type="number" label="Issue price per unit" value={item.price} onChange={event => update(item.id, { price: event.target.value })} inputProps={{ min: 0.000001, step: "0.000001" }} />}
          <FormControlLabel control={<Switch checked={item.accepting} onChange={event => update(item.id, { accepting: event.target.checked })} />} label="Accepting new subscriptions" />
        </div>
      </article>)}
      <Button type="button" onClick={() => { const next = [...classes, blankClass(Math.max(...classes.map(item => item.id)) + 1)]; setClasses(next); publish(next); }}>+ Add unit class</Button>
    </>}
  </section>;
}
